import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, Check, ChevronDown, Download, Menu, X, Target, Lightbulb,
  TrendingUp, Handshake, Cloud, Server, Brain, Code2, Shield, Database,
  Clock, Award, Users, Star, Quote, Sparkles, Zap, Building2, Play,
  ArrowUpRight, CircleDot, Layers, GitBranch, Rocket, Briefcase,
  UserCheck, ThumbsUp, Calendar, FileCheck, BarChart, ClipboardList,
  FileText, GitPullRequest, CheckCircle, Timer, DollarSign, Package,
  Settings, RefreshCw, Layout, Monitor, ShieldCheck, Zap as Lightning,
  CalendarDays, Gauge, PieChart, Rocket as RocketIcon, AlertTriangle,
  Sparkles as SparklesIcon, Layers as LayersIcon, Smartphone, Code,
  Cloud as CloudIcon, Database as DatabaseIcon, PenTool, 
} from 'lucide-react';
import { Link } from 'react-router-dom';

// ============ COLOR PALETTE ============
const COLORS = {
  primary: '#001534',
  secondary: '#FFFFFF',
  accent: '#6366F1',
  accentLight: '#818CF8',
  accentDark: '#4F46E5',
  purple: '#A855F7',
  cyan: '#06B6D4',
  white: '#FFFFFF',
  black: '#000000',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  gray900: '#111827',
  gradientStart: '#6366F1',
  gradientEnd: '#A855F7',
};

// ============ ANIMATED COUNTER ============
const AnimatedCounter = ({ end, suffix = '', prefix = '', duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const increment = end / (duration * 60);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [inView, end, duration]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

// ============ HERO ============
const Hero = () => {
  return (
    <section
      className="relative min-h-[90vh] flex items-center overflow-hidden"
      style={{ backgroundColor: COLORS.primary }}
    >
      {/* Background gradients */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full opacity-20 blur-[120px]"
          style={{ backgroundColor: COLORS.accent }}
        />
        <div
          className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full opacity-20 blur-[120px]"
          style={{ backgroundColor: COLORS.purple }}
        />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(${COLORS.white} 1px, transparent 1px), linear-gradient(90deg, ${COLORS.white} 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-[1.05] tracking-wide mb-6">
              <span style={{ color: COLORS.white }}>Contract Staffing Solutions</span>
              <br />
              <span style={{ color: COLORS.white }}>for </span>
              <span
                className="relative inline-block"
                style={{
                  background: `linear-gradient(135deg, ${COLORS.accentLight}, ${COLORS.purple})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Project-Based Teams
              </span>
            </h1>

            <p className="text-lg md:text-xl leading-relaxed mb-10 max-w-xl" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Scale your workforce flexibly. Access vetted IT contractors for 3-6 month projects without long-term commitment.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <button
                className="group flex items-center gap-2 px-7 py-4 rounded-xl font-semibold text-base transition-all hover:shadow-2xl hover:shadow-indigo-500/40"
                style={{
                  background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`,
                  color: COLORS.white,
                }}
              >
                Get Contract Talent
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                className="flex items-center gap-2 px-7 py-4 rounded-xl font-semibold text-base border transition-all"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: COLORS.white,
                }}
              >
                Learn How It Works
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: 72, suffix: ' hrs', label: 'Average Time to Start' },
                { value: 1000, suffix: '+', label: 'Contractors Placed' },
                { value: 95, suffix: '%', label: 'Extension Rate' },
                { value: 4.9, suffix: '/5', label: 'Contractor Rating' },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="border-l-2 pl-4"
                  style={{ borderColor: COLORS.accent }}
                >
                  <div className="text-3xl font-semibold" style={{ color: COLORS.white }}>
                    <AnimatedCounter end={stat.value} suffix={stat.suffix} prefix={stat.prefix || ''} />
                  </div>
                  <div className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero Image */}
          <div className="lg:col-span-6 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://ik.imagekit.io/7wqj9br0b/KapilTech_Website_Staffing/Staff%20Augementation/contractstaffing.jpg"
                alt="Contract team collaboration"
                className="w-full h-full object-fit"
                loading="lazy"
              />
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(168, 85, 247, 0.2))`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ WHY CONTRACT STAFFING ============
const WhyContractStaffing = () => {
  const features = [
    {
      icon: Lightning,
      title: 'Quick Onboarding',
      desc: 'Get experienced contractors up and running in days, not weeks. Our vetted professionals hit the ground running with minimal ramp-up time.',
      color: COLORS.accent,
    },
    {
      icon: DollarSign,
      title: 'Cost Control',
      desc: 'Pay only for the duration you need. No overhead costs for benefits, training, or long-term commitments. Perfect for budget-conscious projects.',
      color: COLORS.purple,
    },
    {
      icon: Target,
      title: 'Project-Specific Skills',
      desc: 'Hire contractors with exact expertise for your project. Whether you need AWS specialists or React experts, we match the right skills to your needs.',
      color: COLORS.cyan,
    },
    {
      icon: TrendingUp,
      title: 'Team Scaling',
      desc: 'Quickly scale up during peak seasons and down during slow periods. Maintain flexibility without HR overhead or termination complications.',
      color: '#F59E0B',
    },
  ];

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{ backgroundColor: COLORS.secondary }}
      id="why-contract"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-7xl mx-auto mb-20"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.primary }}>
            Why Choose Contract Staffing?
          </h2>
          <p className="text-lg leading-relaxed max-w-3xl mx-auto" style={{ color: COLORS.gray600 }}>
            Contract staffing provides flexibility to scale your team up or down based on project demands. No long-term commitments, no overhead costs, and access to specialized talent on demand.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {features.map((feat, i) => {
            const Icon = feat.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                className="group relative p-8 md:p-10 rounded-3xl border transition-all overflow-hidden"
                style={{
                  backgroundColor: COLORS.gray50,
                  borderColor: COLORS.gray200,
                }}
              >
                <div
                  className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-10 blur-3xl transition-opacity group-hover:opacity-20"
                  style={{ backgroundColor: feat.color }}
                />

                <div className="relative">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                    style={{
                      background: `linear-gradient(135deg, ${feat.color}30, ${feat.color}10)`,
                      border: `1px solid ${feat.color}40`,
                    }}
                  >
                    <Icon className="w-7 h-7" style={{ color: feat.color }} />
                  </div>

                  <h3 className="text-2xl font-bold mb-4" style={{ color: COLORS.primary }}>
                    {feat.title}
                  </h3>
                  <p className="leading-relaxed" style={{ color: COLORS.gray600 }}>
                    {feat.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============ CONTRACT MODELS ============
const ContractModels = () => {
  const models = [
    {
      icon: CalendarDays,
      title: 'Short-Term (1-3 Months)',
      desc: 'Quick skill gap filling, temporary team augmentation, sprint-based projects. Ideal for urgent needs and proof-of-concept projects.',
      color: COLORS.accent,
    },
    {
      icon: Calendar,
      title: 'Medium-Term (3-6 Months)',
      desc: 'Project-based development, feature launches, system migrations. Most common contract duration with proven productivity.',
      color: COLORS.purple,
    },
    {
      icon: RefreshCw,
      title: 'Extended (6-12 Months)',
      desc: 'Long-running projects, product development, major initiatives. Maintains flexibility while providing team stability.',
      color: COLORS.cyan,
    },
    {
      icon: Clock,
      title: 'Ad-Hoc Support',
      desc: 'As-needed support, specific deliverables, consulting-based engagement. Pay for output, not time on staff.',
      color: '#F59E0B',
    },
  ];

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{ backgroundColor: COLORS.primary }}
      id="contract-models"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-5xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.white }}>
            Contract Staffing Models We Offer
          </h2>
          <p className="text-lg text-center" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Choose the contract model that fits your project timeline and budget.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {models.map((model, i) => {
            const Icon = model.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                className="group relative p-8 rounded-3xl border transition-all overflow-hidden"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  borderColor: 'rgba(255,255,255,0.1)',
                }}
              >
                <div
                  className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-10 blur-3xl transition-opacity group-hover:opacity-20"
                  style={{ backgroundColor: model.color }}
                />

                <div className="relative text-center">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{
                      background: `${model.color}20`,
                      border: `1px solid ${model.color}40`,
                    }}
                  >
                    <Icon className="w-7 h-7" style={{ color: model.color }} />
                  </div>

                  <h3 className="text-lg font-bold mb-3" style={{ color: COLORS.white }}>
                    {model.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    {model.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============ WHEN TO USE CONTRACT STAFFING ============
const WhenToUse = () => {
  const scenarios = [
    {
      icon: RocketIcon,
      title: 'Product Launch',
      desc: 'Need extra hands for a 6-month product launch? Contract developers provide the boost without permanent overhead.',
      color: COLORS.accent,
    },
    {
      icon: AlertTriangle,
      title: 'Skill Gap Filling',
      desc: 'Missing Kubernetes expertise for cloud migration? Contract specialists bridge the gap without hiring full-time.',
      color: COLORS.purple,
    },
    {
      icon: TrendingUp,
      title: 'Seasonal Scaling',
      desc: 'E-commerce platform needing extra capacity during holidays? Scale temporarily without permanent team expansion.',
      color: COLORS.cyan,
    },
    {
      icon: RefreshCw,
      title: 'System Migration',
      desc: 'Migrating from legacy to cloud? Contract experts accelerate timeline and ensure project success.',
      color: '#F59E0B',
    },
    {
      icon: Zap,
      title: 'Crisis Response',
      desc: 'Emergency staffing needs? Contract team members can start within days to support urgent projects.',
      color: '#10B981',
    },
    {
      icon: SparklesIcon,
      title: 'MVP Development',
      desc: 'Building a startup MVP? Contract developers reduce risk and validate ideas before permanent hiring.',
      color: '#EC4899',
    },
  ];

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{ backgroundColor: COLORS.secondary }}
      id="when-to-use"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.primary }}>
            When to Use Contract Staffing
          </h2>
          <p className="text-lg text-center" style={{ color: COLORS.gray600 }}>
            Contract staffing is ideal for these common business scenarios.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {scenarios.map((scenario, i) => {
            const Icon = scenario.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ y: -4 }}
                className="group p-8 rounded-3xl border transition-all overflow-hidden"
                style={{
                  backgroundColor: COLORS.gray50,
                  borderColor: COLORS.gray200,
                }}
              >
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                  style={{
                    background: `${scenario.color}20`,
                    border: `1px solid ${scenario.color}40`,
                  }}
                >
                  <Icon className="w-6 h-6" style={{ color: scenario.color }} />
                </div>

                <h3 className="text-xl font-bold mb-2" style={{ color: COLORS.primary }}>
                  {scenario.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: COLORS.gray600 }}>
                  {scenario.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============ AVAILABLE TECHNOLOGY SPECIALIZATIONS ============
const TechSpecializations = () => {
  const specializations = [
    {
      icon: Layout,
      title: 'Frontend Development',
      desc: 'React, Vue, Angular, TypeScript, CSS specialists with modern web development expertise.',
      color: COLORS.accent,
    },
    {
      icon: Code,
      title: 'Backend & APIs',
      desc: 'Node.js, Python, Java, Go developers. REST APIs, microservices, database optimization.',
      color: COLORS.purple,
    },
    {
      icon: CloudIcon,
      title: 'Cloud & DevOps',
      desc: 'AWS, Azure, GCP specialists. CI/CD, infrastructure as code, Kubernetes, containerization.',
      color: COLORS.cyan,
    },
    {
      icon: DatabaseIcon,
      title: 'Data & Analytics',
      desc: 'Data engineers, ETL specialists, analytics engineers. Spark, SQL, Python, big data platforms.',
      color: '#F59E0B',
    },
    {
      icon: Smartphone,
      title: 'Mobile Development',
      desc: 'iOS, Android, React Native contractors. Native and cross-platform mobile experts.',
      color: '#10B981',
    },
    {
      icon: ShieldCheck,
      title: 'QA & Testing',
      desc: 'Automation engineers, QA leads, performance testers. Selenium, Jest, manual testing expertise.',
      color: '#EC4899',
    },
  ];

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{ backgroundColor: COLORS.primary }}
      id="tech-specializations"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-5xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.white }}>
            Available Technology Specializations
          </h2>
          <p className="text-lg text-center" style={{ color: 'rgba(255,255,255,0.7)' }}>
            We maintain a network of contract professionals across all major tech stacks.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {specializations.map((spec, i) => {
            const Icon = spec.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ y: -4, scale: 1.02 }}
                className="group relative p-8 rounded-3xl border transition-all overflow-hidden"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  borderColor: 'rgba(255,255,255,0.1)',
                }}
              >
                <div
                  className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-10 blur-3xl transition-opacity group-hover:opacity-20"
                  style={{ backgroundColor: spec.color }}
                />

                <div className="relative text-center">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{
                      background: `${spec.color}20`,
                      border: `1px solid ${spec.color}40`,
                    }}
                  >
                    <Icon className="w-7 h-7" style={{ color: spec.color }} />
                  </div>

                  <h3 className="text-lg font-bold mb-2" style={{ color: COLORS.white }}>
                    {spec.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    {spec.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============ PROCESS ============
const Process = () => {
  const steps = [
    {
      number: '1',
      title: 'Requirements Gathering',
      desc: 'Understand your project scope, duration, required skills, and team integration needs. Usually completed same-day.',
      icon: Target,
      color: COLORS.accent,
    },
    {
      number: '2',
      title: 'Rapid Sourcing',
      desc: 'Access our pre-vetted contractor pool. 24-48 hours to present qualified candidates with work samples.',
      icon: Users,
      color: COLORS.purple,
    },
    {
      number: '3',
      title: 'Technical Assessment',
      desc: 'All contractors pass technical screening. Code reviews, project walkthroughs, and skill validation included.',
      icon: CheckCircle,
      color: COLORS.cyan,
    },
    {
      number: '4',
      title: 'Fast Onboarding',
      desc: 'Start within 5-7 days. Minimal ramp-up with documentation, code access, and quick integration into your team.',
      icon: Rocket,
      color: '#F59E0B',
    },
  ];

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{ backgroundColor: COLORS.secondary }}
      id="process"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.primary }}>
            Our Contract Staffing Process
          </h2>
          <p className="text-lg text-center" style={{ color: COLORS.gray600 }}>
            Fast-track your project with our streamlined contract placement process.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-4 gap-6">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                className="relative p-6 rounded-3xl border text-center transition-all"
                style={{
                  backgroundColor: COLORS.gray50,
                  borderColor: COLORS.gray200,
                }}
              >
                {/* Connector line */}
                {i < steps.length - 1 && (
                  <div
                    className="hidden md:block absolute top-1/2 -right-3 w-6 h-px"
                    style={{ backgroundColor: COLORS.gray300 }}
                  />
                )}

                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{
                    background: `linear-gradient(135deg, ${step.color}30, ${step.color}10)`,
                    border: `1px solid ${step.color}40`,
                  }}
                >
                  <Icon className="w-6 h-6" style={{ color: step.color }} />
                </div>

                <div
                  className="text-md font-bold mb-2"
                  style={{ color: step.color }}
                >
                  Step {step.number}
                </div>

                <h3 className="text-md font-bold mb-2" style={{ color: COLORS.primary }}>
                  {step.title}
                </h3>
                <p className="text-md leading-relaxed" style={{ color: COLORS.gray600 }}>
                  {step.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============ FAQ ============
const FAQ = () => {
  const [open, setOpen] = useState(0);
  const faqs = [
    {
      q: 'How quickly can you provide contract developers?',
      a: 'We typically present qualified candidates within 24-48 hours of receiving your requirements. Onboarding usually takes 5-7 days. For urgent needs, we can expedite to 48-72 hour start dates with pre-vetted contractors from our immediate pool.',
    },
    {
      q: 'What contract durations do you support?',
      a: 'We handle contracts from 1 month to 12 months. Most common engagements are 3-6 months. We also offer ad-hoc/hourly arrangements for flexible staffing needs with no minimum commitment.',
    },
    {
      q: 'What are the typical costs for contract staffing?',
      a: 'Contract costs vary by skill level, experience, and technology. Typically 20-40% higher hourly rate than permanent salaries (no benefits/overhead). For example: mid-level developer ($40-50/hr), senior architect ($65-85/hr). We provide transparent quotes before engagement.',
    },
    {
      q: 'Can we extend a contract or transition to permanent?',
      a: 'Absolutely! Many clients convert successful contractors to permanent roles using our Contract-to-Hire service. We handle extensions seamlessly with no gaps. Discuss transition plans early if you anticipate this possibility.',
    },
    {
      q: 'Do you handle contract compliance and paperwork?',
      a: 'Yes, we manage all contracting, compliance, taxes, and legal documentation. You work directly with our contractor team—we handle all backend administration and ensure compliance with labor laws and contract terms.',
    },
    {
      q: 'What if the contractor isn\'t the right fit?',
      a: 'Within the first two weeks (trial period), if there\'s a skills mismatch, we replace the contractor at no additional cost. We\'re committed to your satisfaction and won\'t charge extra for replacements during this period.',
    },
  ];

  return (
    <section className="relative py-16 overflow-hidden" style={{ backgroundColor: COLORS.primary }} id="faq">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.secondary }}>
            Contract Staffing FAQs
          </h2>
        </motion.div>

        <div className="space-y-3 bg-white p-5 rounded-3xl border">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="rounded-2xl border overflow-hidden transition-all"
              style={{
                backgroundColor: '#ffffff',
                borderColor: open === i ? 'rgba(99, 102, 241, 0.3)' : COLORS.gray200,
              }}
            >
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 p-6 text-left"
              >
                <span className="font-semibold text-base md:text-lg" style={{ color: COLORS.primary }}>
                  {faq.q}
                </span>
                <motion.div
                  animate={{ rotate: open === i ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: open === i ? COLORS.accent : COLORS.gray200 }}
                >
                  <ChevronDown className="w-4 h-4" style={{ color: open === i ? COLORS.white : COLORS.primary }} />
                </motion.div>
              </button>

              <AnimatePresence>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 leading-relaxed text-justify" style={{ color: COLORS.gray600, wordBreak: 'normal', overflowWrap: 'normal', hyphens: 'none', whiteSpace: 'normal' }}>
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ FINAL CTA ============
const FinalCTA = () => {
  const COLORS = {
    bg: '#F8FAFC',
    heading: '#0F172A',
    muted: '#475569',
    accent: '#6366F1',
    accentLight: '#A5B4FC',
    white: '#FFFFFF',
    border: '#E2E8F0',
  };

  return (
    <section
      className="relative py-24 overflow-hidden"
      style={{ backgroundColor: COLORS.bg }}
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6"
            style={{ color: COLORS.heading }}
          >
            Flexible Staffing for Your Project Needs
          </h2>

          <p
            className="text-base md:text-lg max-w-2xl mx-auto mb-10"
            style={{ color: COLORS.muted }}
          >
            Connect with experienced contractors ready to deliver results on your timeline.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              onClick={() => window.scrollTo(0, 0)}
              whileHover={{ scale: 1.03, y: -1 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-base"
              style={{
                backgroundColor: COLORS.accent,
                color: COLORS.white,
              }}
            >
              Start Your Contract Search
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============ MAIN APP ============
export default function ContractStaffing() {
  return (
    <div style={{ backgroundColor: COLORS.secondary, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Hero />
      <WhyContractStaffing />
      <ContractModels />
      <WhenToUse />
      <TechSpecializations />
      <Process />
      <FAQ />
      <FinalCTA />
    </div>
  );
}
